import { useCallback, useRef, useState } from "react";
import useAxios from "./useAxios";
import { TLoadDataParams } from "../../types/common";

const requestSignature = (options: TLoadDataParams) => {
  const { api, id, params, isExternal, method } = options;
  const sortedParams =
    params && typeof params === "object"
      ? Object.keys(params)
          .sort()
          .reduce((acc: any, key) => {
            acc[key] = params[key];
            return acc;
          }, {})
      : params;

  return JSON.stringify({ api, id, params: sortedParams, isExternal, method });
};

const useGet = () => {
  const [loading, setLoading] = useState(false);
  const [refreshing, setrefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [data, setData] = useState<any>();
  const { get } = useAxios();

  const getRef = useRef(get);
  getRef.current = get;

  const inFlightRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);

  const loadData = useCallback((options: TLoadDataParams) => {
    const { api, isExternal, params, method, id } = options;

    const signature = requestSignature(options);
    if (inFlightRef.current === signature) return;
    inFlightRef.current = signature;

    const requestId = ++requestIdRef.current;
    const isCurrent = () => requestId === requestIdRef.current;

    setData(null);
    setSuccess(false);
    setLoading(true);

    // If id is passed get single instance, other fetch list
    getRef.current({
      api: id ? `${api}${id}/` : api,
      params,
      isExternal,
      loader: (isLoading: boolean) => {
        if (isCurrent()) setLoading(isLoading);
      },
      errorHandler: (err: any) => {
        if (!isCurrent()) return;
        inFlightRef.current = null;
        setError(err);
      },
      successHandler: (data: any) => {
        if (!isCurrent()) return;
        inFlightRef.current = null;
        setData(data);
        setSuccess(true);
      },
      methodName: method,
    });
  }, []);

  const refreshData = useCallback((options: TLoadDataParams) => {
    const { api, isExternal, params } = options;
    setData(null);
    setrefreshing(true);
    // If id is passed get single instance, other fetch list
    getRef.current({
      api,
      isExternal,
      loader: setrefreshing,
      errorHandler: setError,
      successHandler: setData,
      params,
    });
  }, []);

  const reset = useCallback(() => {
    inFlightRef.current = null;
    requestIdRef.current += 1;
    setLoading(false);
    setError(false);
    setSuccess(false);
    setData(undefined);
  }, []);

  return {
    loading,
    refreshing,
    error,
    data,
    success,
    loadData,
    refreshData,
    reset,
  };
};

export default useGet;
